import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { usePage } from '../../../context/PageContext';
import AddEditTransactionHeader from './addEditTransactionHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOCAL_STORAGE_NAMES, ROUTES } from '../../../constants/constants';
import { IMatter } from '../../../interface/matter';
import { Spin } from '../../Ant';
import { Flex } from 'antd';
import { ItemDetails, MailMetadata } from '../../../interface/mailMetadata';
import { useFetchMatterById } from '../../../hooks/useFetchMatterById';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { AnalyticsPage } from '../../../interface/analytics';
import { getAllMailRecipients } from '../../../constants/utilityFunctions';
import EntriesManagementTimer from '../../timer/entriesManagementTimer';
import { ITimer } from '../../../interface/timer';
import { useEmailCategoryTagger } from '../../../hooks/useEmailCategoryTagger';
import { useTransactionMailActions } from '../../../hooks/useTransactionMailActions';
import CustomTabs from '../../customTabs/customTabs';
import EntriesTabMail from './entriesTabMail';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import MatterRequestMail from './matterRequestMail';
import { useManageMatterRequestContext } from '../../../context/ManageMatterRequest';
import RenderTransactionRestrictions from './renderTransactionRestrictions';
import { ITransaction } from '../../../interface/transaction';
import { useGraphAuthStatus } from '../../../hooks/useGraphAuthStatus';
import { useFetchUserParameters } from '../../../hooks/useFetchUserParameters';
import { ProductName } from '../../../interface/system';
import { ParameterEntityType } from '../../../interface/parameters';
import { useSystemInfo } from '../../../context/SystemInfoContext';
import { useRestrictedUserMatterPermissions } from '../../../hooks/useRestrictedUserMatterPermissions';
import GraphReauthBanner from './graphReauthBanner';

const TransactionMail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateTop } = useNavigationStack();
  const { updateManageMatterRequest } = useManageMatterRequestContext();
  const { trackPage } = useAnalytics();
  const { t } = useTranslation();
  const [mailMetadata, setMailMetadata] = useState<MailMetadata | null>(null);
  const [itemDetails, setItemDetails] = useState<ItemDetails | null>(null);
  const { setTitle } = usePage();
  const { getCustomMetadataFromEmail, getFolderNameForItem, getItemId } = useEmailCategoryTagger();
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const emailFolderNameRef = useRef<string>('');
  const [mailSubject, setMailSubject] = useState<string>('');
  const [sentDate, setSentDate] = useState<string>('');
  const [selectedMatter, setSelectedMatter] = useState<IMatter | undefined>(undefined);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [tabsArea, setTabsArea] = useState<HTMLDivElement | null>(null);
  const [activeMailTab, setActiveMailTab] = useState<string>(location.state?.fromTab ?? 'entries');
  const [pendingValidationTransaction, setPendingValidationTransaction] =
    useState<ITransaction | null>(null);
  const validationResolveRef = useRef<((value: unknown) => void) | null>(null);
  const { needsReauth } = useGraphAuthStatus();
  const { systemParams } = useSystemInfo();
  const { canCreateMatterRequests } = useRestrictedUserMatterPermissions();
  const { data: parametersDetails } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.FEATURES_IN_USE,
  });
  const showMatterRequests =
    parametersDetails?.parameters?.featuresInUseParameters?.matterRequestInUse &&
    systemParams?.systemInfo?.productName !== ProductName.LAKIMIES;
  const showMatterRequestTab =
    showMatterRequests && (canCreateMatterRequests || Boolean(mailMetadata?.matterRequestId));
  useEffect(() => {
    updateTop({ fromTab: activeMailTab });
  }, [activeMailTab]);
  let itemType: string | undefined;
  if (Office.context?.mailbox?.item) {
    itemType = Office.context.mailbox.item.itemType;
  }

  const isAppointment = itemType === Office.MailboxEnums.ItemType.Appointment;
  const {
    data: matterDetails,
    isLoading: isMatterDetailsLoading,
    error: matterDetailsError,
  } = useFetchMatterById({
    withFavoriteInformation: true,
    withDefaultTransactionType: true,
    withShowOnlyOwnEntries: true,
    withHasLinkedTransactionTypes: true,
    skipGlobalErrorHandler: true,
    id: selectedMatter?.id,
    relations: ['principalCompany:name', 'principalPrivatePerson:fullName'],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'language',
      'currency',
      'conflictCheckDoneDate',
      'hasImportantNotes',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.ENTRIES_MANAGEMENT);
  }, [trackPage]);

  const fetchMetaData = async () => {
    const item = Office.context.mailbox.item;
    const itemId = item?.itemId ?? (await getItemId(item));
    if (itemId) {
      const restId = Office.context.mailbox.convertToRestId(
        itemId,
        Office.MailboxEnums.RestVersion.v2_0,
      );
      try {
        const details = await getCustomMetadataFromEmail(restId);
        const folderName = await getFolderNameForItem(restId);
        emailFolderNameRef.current = folderName;
        setItemDetails(details?.itemDetails as ItemDetails);
        setMailMetadata(details?.customExtension ?? null);
        if (details?.customExtension?.matterId) {
          setSelectedMatter({ id: details?.customExtension.matterId } as IMatter);
        }
      } catch (error) {
        // Microsoft Graph mailbox metadata is unavailable, most likely because access needs to be
        // reauthorized. GraphReauthBanner surfaces that to the user; nothing else to do here.
        console.error('Failed to fetch mail metadata from Microsoft Graph:', error);
      }
    }
  };

  const updateSubject = async () => {
    const item = Office.context.mailbox.item;
    if (
      item &&
      (item.itemType === Office.MailboxEnums.ItemType.Message ||
        item.itemType === Office.MailboxEnums.ItemType.Appointment)
    ) {
      const sentDate = item.dateTimeCreated;
      if (sentDate) {
        setSentDate(sentDate.toString());
      }
      const currentTimer = timers.find((t) => t.itemId === item?.itemId) ?? undefined;
      if (currentTimer?.subject) {
        setMailSubject(currentTimer?.subject ?? '');
        return;
      }
      if (item?.subject?.getAsync) {
        item.subject.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            setMailSubject(result.value);
          }
        });
      } else if (item.subject) {
        setMailSubject(item.subject);
      }
    }
  };

  const performInitialFetch = () => {
    getAllMailRecipients(emailFolderNameRef.current).then(setEmailRecipients);
    updateSubject();
    fetchMetaData();
  };

  useEffect(() => {
    const intervalId = setInterval(updateSubject, 2000);
    Office.onReady(() => {
      performInitialFetch();
      Office.context.mailbox.addHandlerAsync(
        Office.EventType.ItemChanged,
        () => {
          performInitialFetch();
        },
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            console.error('Failed to add event listener:', result.error);
          }
        },
      );
    });

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleBack = () => {
    updateManageMatterRequest(null);
    navigate(ROUTES.DASHBOARD);
  };

  const handleValidateRestrictions = async (transaction: ITransaction): Promise<unknown> => {
    return new Promise((resolve) => {
      setPendingValidationTransaction(transaction);
      validationResolveRef.current = resolve;
    });
  };

  const onSelectEntity = (result?: ITransaction) => {
    const resolve = validationResolveRef.current;
    setPendingValidationTransaction(null);
    validationResolveRef.current = null;
    if (resolve) {
      resolve(result);
    }
  };

  const timers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const currentTimer = timers.find((t) => t.subject === mailSubject) ?? undefined;
  const renderTimer = () => {
    return <EntriesManagementTimer subject={mailSubject} selectedMatter={selectedMatter} />;
  };

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleHome={handleBack}
        title={isAppointment ? t('common:calendarActions') : t('common:emailActions')}
        renderTimer={needsReauth ? undefined : renderTimer}
      />,
    );
  }, [mailSubject, selectedMatter, currentTimer?.duration, needsReauth]);

  const {
    linkToMatterLoading,
    handleAddTransaction,
    handleAddTask,
    handleLinkToMatter,
    handleUnlinkToMatter,
  } = useTransactionMailActions({
    selectedMatter,
    matterDetails,
    mailSubject,
    sentDate,
    itemDetails,
    mailMetadata,
    fetchMetaData,
    setExpanded,
    onLinkSuccess: () => setSelectedMatter(undefined),
    onValidateRestrictions: handleValidateRestrictions,
  });

  const areRecipientsChanged = (prev: string[], next: string[]): boolean => {
    if (next.length !== prev.length) return true;
    return next.some((email, idx) => prev[idx] !== email);
  };

  const handleRecipientsChanged = () => {
    getAllMailRecipients().then((newRecipients) => {
      setEmailRecipients((prev) => {
        return areRecipientsChanged(prev, newRecipients) ? newRecipients : prev;
      });
    });
  };

  const handleRecipientsAddHandlerResult = (result: Office.AsyncResult<void>) => {
    if (result.status !== Office.AsyncResultStatus.Succeeded) {
      console.error('Failed to add RecipientsChanged handler:', result.error);
    }
  };

  const setupOfficeHandlers = () => {
    getAllMailRecipients().then(setEmailRecipients);
    if (Office.context.mailbox.item?.addHandlerAsync) {
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.RecipientsChanged,
        handleRecipientsChanged,
        handleRecipientsAddHandlerResult,
      );
    }
  };

  useEffect(() => {
    Office.onReady(setupOfficeHandlers);
  }, []);

  const entriesTab = (
    <EntriesTabMail
      mailSubject={mailSubject}
      mailMetadata={mailMetadata}
      emailRecipients={emailRecipients}
      selectedMatter={selectedMatter}
      setSelectedMatter={setSelectedMatter}
      expanded={expanded}
      setExpanded={setExpanded}
      matterDetails={matterDetails}
      isMatterDetailsLoading={isMatterDetailsLoading}
      matterDetailsError={matterDetailsError}
      handleAddTransaction={handleAddTransaction}
      handleAddTask={handleAddTask}
      handleLinkToMatter={handleLinkToMatter}
      handleUnlinkToMatter={handleUnlinkToMatter}
      onItemUnlinked={fetchMetaData}
      disableMatterLinking={needsReauth}
    />
  );

  const mailActionTabs = [
    {
      key: 'entries',
      label: t('common:tab.entries'),
      children: entriesTab,
    },
    {
      key: 'matterRequests',
      label: t('matterRequest:matterRequest'),
      children: (
        <MatterRequestMail
          mailSubject={mailSubject}
          mailMetadata={mailMetadata}
          onMatterRequestCreated={() => {
            fetchMetaData();
            updateManageMatterRequest(null, true);
          }}
        />
      ),
    },
  ];

  return (
    <Flex vertical className="h-full">
      {linkToMatterLoading && <Spin fullscreen />}
      <div ref={setTabsArea} className="relative min-h-0 w-full h-full">
        {showMatterRequestTab ? (
          <CustomTabs
            activeKey={activeMailTab}
            onChange={setActiveMailTab}
            items={mailActionTabs}
            className="min-h-0"
          />
        ) : (
          /* standing in for the tab pane, which is what lays in the top gutter when tabs are shown */
          <div className="min-h-0 w-full h-full pt-gutter">{entriesTab}</div>
        )}
        {tabsArea && <GraphReauthBanner container={tabsArea} />}
      </div>
      {pendingValidationTransaction && (
        <RenderTransactionRestrictions
          transaction={pendingValidationTransaction}
          onSelectTransaction={onSelectEntity}
        />
      )}
    </Flex>
  );
};

export default TransactionMail;
